<template>
  <div class="faq-element py-16 !m-0" aria-labelledby="products-title">
    <div class="faq-inner">
      <div class="title" v-if="introText || introSubText">
        <span class="sub-heading text-base 3xl:text-md font-marcellus text-purple" v-if="introText">{{ introText }}</span>
        <h2 id="products-title" class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none" v-if="introSubText">
          {{ introSubText }}
        </h2>
      </div>
      <div class="content mt-7 lg:mt-10">
        
        <div
          class="relative block md:hidden h-auto xl:h-[600px] order-1 xl:order-2 bg-gray-100 aspect-[16/9] md:aspect-square rounded-[15px] md:rounded-[20px] overflow-hidden"
          v-if="imageMobile || imageTablet || imageDesktop">
          <SharedImage
            :src="imageDesktop?.media?.url"
            :alt="imageDesktop?.media?.alt ?? $t('mySofaBed')"
            :width="imageDesktop?.media?.metaData?.width"
            :height="imageDesktop?.media?.metaData?.height"
            loading="lazy"
            imgClass="w-full h-full object-cover"
            :sizes="{
              desktop: '1620x709',
              tablet: '920x403',
              mobile: '680x382'
            }"
            :sources="{
              desktop: imageDesktop?.media,
              tablet: imageTablet?.media,
              mobile: imageMobile?.media
            }"
          />
        </div>

        <div class="block mt-7 md:mt-0" v-if="descriptionText" v-html="descriptionText">
        </div>
        
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-12 items-start">
          <!-- Left Column - Accordion -->
          <div class="accordion accordion-container mt-4 xl:mt-0 order-2 xl:order-1" v-if="faqItems">
            
            <div 
            class="border-t border-gray-300 collapse-item" 
            :class="activeFaq === index ? 'active' : ''"
              v-for="(faqItem, index) in faqItems"
              :key="index">
              <button 
                @click="toggleAccordion(index)"
                class="py-4 flex bg-transparent border-0 w-full items-center justify-between text-left cursor-pointer">
                <span class="font-marcellus text-md md:text-lg 3xl:text-xl max-w-[90%]"
                  > {{ faqItem.question }}</span
                >
                <i
                  :class="activeFaq === index ? 'rotate-180' : ''"
                  class="icon-chevron-down w-5 h-5 text-gray-500 transform transition-transform duration-200 font-bold"></i>
              </button>
              <div class="accordion-content overflow-hidden transition-all duration-300 max-h-0" 
                :style="{ maxHeight: activeFaq === index ? contentHeights[index] + 'px' : '0px' }"
                v-if="faqItem.answer"
                :data-faq-index="index"
                 >
                  <CmsTextRender class="accordian-inner pb-8 pt-3 [&_>*+*]:mt-[15px] [&_p+p]:mt-[15px] msb-cms-text-content" :content="{ data: { text: faqItem.answer } }" :is-faq-section="true" :is-showroom-tab-section="index === 0 ? true : false" />
              </div>
            </div>

          </div>

          <!-- Right Column - Image -->
          <div
            class="relative hidden md:block h-[320px] xl:h-[600px] order-1 xl:order-2 bg-gray-100 rounded-[15px] md:rounded-[20px] overflow-hidden"
            v-if="imageDesktop || imageTablet || imageMobile">
            <SharedImage
              :src="imageDesktop?.media?.url"
              :alt="imageDesktop?.media?.alt ? imageDesktop?.media?.alt : 'my sofa bed'"
              :width="imageDesktop?.media?.metaData?.width"
              :height="imageDesktop?.media?.metaData?.height"
              loading="lazy"
              imgClass="w-full h-full object-cover"
              :sizes="{
                desktop: '780x660',
                tablet: '744x480',
                mobile: '586x840'
              }"
              :sources="{
                desktop: imageDesktop?.media,
                tablet: imageTablet?.media,
                mobile: imageMobile?.media
              }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CmsElementInfoFaq } from "~/composables/useCustom";
import CmsTextRender from '@/components/shared/CmsTextRender.vue';
import { fa } from "intl-tel-input/i18n";

const props = defineProps<{ content: CmsElementInfoFaq }>();

const activeFaq = ref(0);
const contentHeights = ref<{ [key: number]: number }>({});

const { introText, introSubText, descriptionText, imageDesktop, imageMobile, imageTablet, faqItems } = props.content.data;

const calculateHeight = (index: number) => {
  const content = document.querySelector(`[data-faq-index="${index}"] .accordian-inner`);
  if (content) {
    contentHeights.value[index] = content.scrollHeight;
  }
};

const toggleAccordion = async (index: number) => {
  if (activeFaq.value === index) {
    activeFaq.value = -1;
  } else {
    activeFaq.value = index;
    // Wait for the next DOM update
    await nextTick();
    calculateHeight(index);
  }
};

onMounted(async () => {
  // Wait for initial render
  await nextTick();
  // Calculate heights for all accordion items
  if (faqItems) {
    for (let i = 0; i < faqItems.length; i++) {
      calculateHeight(i);
    }
  }
});
</script>